import { API_BASE, getApiAuthHeaders } from '$lib/constants/api';
import { clearAccessToken, refreshSession } from '$lib/auth';

export type ApiFetchOptions = RequestInit & {
	format?: 'json' | 'xlsx' | 'blob';
};

export async function apiFetch<T>(
	endpoint: string,
	options: ApiFetchOptions = {},
	retried = false
): Promise<T> {
	const { format = 'json', headers, ...fetchOptions } = options;
	const url = `${API_BASE}${endpoint}`;

	const response = await fetch(url, {
		credentials: 'include',
		...fetchOptions,
		headers: {
			...getApiAuthHeaders(),
			...(headers as Record<string, string> | undefined)
		}
	});

	if (response.status === 401 && !retried && !endpoint.startsWith('/auth/')) {
		try {
			await refreshSession();
			return apiFetch<T>(endpoint, options, true);
		} catch {
			clearAccessToken();
			throw new Error('Authentication required. Please log in again.');
		}
	}

	if (!response.ok) {
		const errorData: unknown = await response.json().catch(() => ({}));
		const detail =
			typeof errorData === 'object' &&
			errorData !== null &&
			'detail' in errorData &&
			typeof (errorData as { detail: unknown }).detail === 'string'
				? (errorData as { detail: string }).detail
				: JSON.stringify(errorData);
		throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
	}

	if (response.status === 204) {
		return null as T;
	}

	if (format === 'xlsx' || format === 'blob') {
		const blob = await response.blob();
		return URL.createObjectURL(blob) as T;
	}

	return (await response.json()) as T;
}

export function getApiHeaders(): Record<string, string> {
	return getApiAuthHeaders();
}

export function getErrorMessage(error: unknown): string {
	const message = error instanceof Error ? error.message : String(error ?? '');
	if (message.includes('duplicate_address')) {
		return 'This address is already used. Please choose a different address.';
	}
	if (message.includes('channel_occupied')) {
		return 'This channel is already assigned to another load.';
	}
	if (message.includes('load_already_assigned')) {
		return 'This load is already assigned to another channel.';
	}
	return message || 'An unexpected error occurred.';
}
